// src/pages/ActionablesCalendar.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const STATUS_COLORS = {
  "New": "#3b82f6",                     // blue
  "Completed": "#10b981",               // green
  "Pending": "#ef4444",                 // red
  "Completed After Deadline": "#f59e0b" // amber
};

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

function firstDayOfMonth(year, monthIndex) {
  return new Date(year, monthIndex, 1);
}
function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function buildMonthMatrix(year, monthIndex) {
  const first = firstDayOfMonth(year, monthIndex);
  const firstWeekday = first.getDay();
  const totalDays = daysInMonth(year, monthIndex);

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);

  for (let d = 1; d <= totalDays; d++) {
    cells.push(new Date(year, monthIndex, d));
  }

  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);

  return cells;
}

// Returns YYYY-MM-DD for the given Date using local year/month/day (no timezone conversions)
function formatLocalDate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const ActionablesCalendar = () => {
  const navigate = useNavigate();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [range, setRange] = useState({ startYear: null, endYear: null });
  const [dayMap, setDayMap] = useState({});
  const [loading, setLoading] = useState(false);

  const [username, setUsername] = useState(localStorage.getItem("username") || "");

useEffect(() => {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    setUsername("");
    return;
  }

  axios
    .get("/api/users/me/", {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => {
      setUsername(res.data.username);
      localStorage.setItem("username", res.data.username);
    })
    .catch((err) => console.error("Failed to load username:", err));
}, []);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get("/api/actionables/calendar/", {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      setDayMap(res.data.days || {});
      setRange({ startYear: res.data.start_year, endYear: res.data.end_year });

      if (res.data.start_year && res.data.end_year) {
        const s = res.data.start_year;
        const e = res.data.end_year;
        if (selectedYear < s) setSelectedYear(s);
        if (selectedYear > e) setSelectedYear(e);
      }

    } catch (e) {
      console.error("Error loading calendar data:", e);
      setDayMap({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendar();
  }, []);

  const yearOptions = useMemo(() => {
    if (range.startYear && range.endYear) {
      const arr = [];
      for (let y = range.startYear; y <= range.endYear; y++) arr.push(y);
      return arr;
    }
    const base = new Date().getFullYear();
    return [base, base + 1, base + 2];
  }, [range]);

  const canGoPrev = () => {
    if (!range.startYear) return true;
    const global = selectedYear * 12 + selectedMonth;
    const startGlobal = range.startYear * 12 + 0;
    return global > startGlobal;
  };

  const canGoNext = () => {
    if (!range.endYear) return true;
    const global = selectedYear * 12 + selectedMonth;
    const endGlobal = range.endYear * 12 + 11;
    return global < endGlobal;
  };

  const prevMonth = () => {
    if (!canGoPrev()) return;
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear((y) => y - 1);
    } else {
      setSelectedMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (!canGoNext()) return;
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear((y) => y + 1);
    } else {
      setSelectedMonth((m) => m + 1);
    }
  };

  const getCountsForDate = (d) => {
    if (!d) return null;
    const iso = formatLocalDate(d);
    return dayMap[iso] || null;
  };

  const onClickBadge = (d, status) => {
    if (!d) return;
    const iso = formatLocalDate(d);
    const encoded = encodeURIComponent(status);
    navigate(`/actionables-by-status/${encoded}?date=${iso}`);
  };

  const matrix = buildMonthMatrix(selectedYear, selectedMonth);

  return (
    <div className="cal-container">
      {/* wrapper to center content */}
      <div className="cal-wrapper">

        <div className="header-row">
          <h1 className="header">Actionables Calendar</h1>
          <div className="header-right">
            <div className="user-chip">
              <strong>User:</strong>
              <span className="pill">{username || "Not signed in"}</span>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className="secondary"
                onClick={() => (window.location.href = "/dashboard")}
              >
                ⬅ Back
              </button>

              <button className="secondary" onClick={prevMonth} disabled={!canGoPrev()}>
                Prev
              </button>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                >
                  {MONTH_NAMES.map((m, idx) => (
                    <option key={m} value={idx}>
                      {m}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <button className="secondary" onClick={nextMonth} disabled={!canGoNext()}>
                Next
              </button>

              <button className="primary" onClick={fetchCalendar} disabled={loading}>
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div
          className="controls"
          style={{ justifyContent: "flex-start", marginBottom: 14 }}
        >
          <div className="legend">
            {Object.entries(STATUS_COLORS).map(([status, color]) => (
              <div key={status} className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: color }} />
                <span>{status}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar */}
        <div className="month-big-card">
          <div className="month-big-title">
            {MONTH_NAMES[selectedMonth]} {selectedYear}
          </div>

          <div className="weekdays-big">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
              <div key={w} className="weekday-big">
                {w}
              </div>
            ))}
          </div>

          <div className="days-grid-big">
            {matrix.map((d, i) => {
              const counts = d ? getCountsForDate(d) : null;
              const inMonth = !!d && d.getMonth() === selectedMonth;

              return (
                <div key={i} className={`day-cell-big ${inMonth ? "" : "dim"}`}>
                  {d && <div className="date-num-big">{d.getDate()}</div>}

                  {counts && (
                    <div className="badges-big">
                      {Object.entries(counts).map(([status, count]) => {
                        const color = STATUS_COLORS[status] || "#9ca3af";
                        return (
                          <button
                            key={status}
                            className="badge-big"
                            style={{ borderColor: color, color }}
                            onClick={() => onClickBadge(d, status)}
                          >
                            {status}: {count}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* Styles */}
      <style>{`
        .cal-container {
          padding: 18px;
          font-family: Arial, sans-serif;
          background: #f6f7fb;
        }

        /* center calendar wrapper */
        .cal-wrapper {
          max-width: 1200px;
          margin: auto;
        }

        .header-row {
          display:flex; 
          align-items:center; 
          justify-content:space-between; 
          gap:12px; 
          margin-bottom:8px;
        }
        .header { font-size: 26px; font-weight: 700; color:#1f2937; margin: 0; }
        .header-right { display:flex; gap:10px; align-items:center; }
        .user-chip { display:flex; gap:8px; align-items:center; }
        .pill { background:#eef2ff; border-radius: 999px; padding: 4px 10px; }

        .controls { display:flex; gap:16px; align-items:center; flex-wrap:wrap; margin-bottom:6px; }
        .legend { display:flex; gap:14px; align-items:center; flex-wrap:wrap; }
        .legend-item { display:flex; align-items:center; gap:6px; }
        .legend-dot { width:12px; height:12px; border-radius:50%; display:inline-block; }

        .primary { background:#3b82f6; color:white; border:none; padding:8px 12px; border-radius:8px; cursor:pointer; }
        .secondary { background:#6b7280; color:white; border:none; padding:8px 10px; border-radius:8px; cursor:pointer; }

        .month-big-card {
          background:white;
          border-radius:14px;
          padding:12px;
          box-shadow:0 2px 10px rgba(0,0,0,0.06);
        }

        .month-big-title { font-weight:600; margin-bottom:8px; color:#111827; font-size:18px; }
        .weekdays-big { display:grid; grid-template-columns: repeat(7, 1fr); margin-bottom:6px; }
        .weekday-big { font-size:13px; text-align:center; color:#6b7280; font-weight:600; padding:6px 0; }

        .days-grid-big { display:grid; grid-template-columns: repeat(7, 1fr); gap:8px; }

        .day-cell-big {
          min-height:120px;
          background:#f9fafb;
          border-radius:10px;
          padding:8px;
          border:1px solid #e5e7eb;
          display:flex;
          flex-direction:column;
          justify-content:flex-start;
          position:relative;
        }

        .day-cell-big.dim { background:#f3f4f6; color:#9ca3af; }
        .date-num-big { position:absolute; top:6px; right:8px; font-size:13px; color:#374151; font-weight:700; }

        .badges-big {
          display:flex;
          flex-direction:column;
          gap:8px;
          margin-top:20px;
          align-items:flex-start;
        }

        .badge-big {
          border: 1px solid;
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 13px;
          font-weight: 700;
          background: white;
          cursor: pointer;
        }
        .badge-big:hover { 
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.08);
        }

        @media (max-width: 900px) {
          .day-cell-big { min-height: 90px; }
          .badge-big { font-size: 12px; padding: 5px 8px; }
        }

        @media (max-width: 640px) {
          .weekdays-big { font-size: 11px; }
          .days-grid-big { gap:6px; }
          .day-cell-big { min-height: 80px; padding:6px; }
          .date-num-big { font-size:12px; }
        }
      `}</style>
    </div>
  );
};

export default ActionablesCalendar;
